import React, { useMemo } from 'react'
import {
  Route,
  Switch,
  withRouter,
  Redirect,
  RouteComponentProps,
} from 'react-router-dom'
import { useApplicationContext, AnimatedProgressBar } from '@adc/ui-adc'
import StepComponent from 'common/components/sellerFlow/steps/step'
import {
  sellerFlowPath,
  sellerSteps,
  Step,
} from 'common/components/sellerFlow/steps'
import classnames from 'classnames/bind'
import { NextStepData, StepId } from 'common/components/sellerFlow/steps/types'
import { getInitialLeadIntake } from 'common/components/sellerFlow/steps/utils'
import {
  StepManagerProvider,
  useStepManagerContext,
} from '../../components/sellerFlow/steps/stepManager'
import styles from './styles.scss'
const cx = classnames.bind(styles)

export type SellerFlowRoutesProps = {
  featureFlags: Record<string, unknown>
  initialLeadData: {
    address: Record<string, string>
  }
  initialLeadValidations?: Record<string, string>
} & RouteComponentProps

const FlowProgressBar = () => {
  const { data } = useStepManagerContext()
  const { progress, currentStep } = data
  if (currentStep.id === StepId.Address) {
    return null
  }

  return (
    <AnimatedProgressBar
      percent={progress < 1 ? 1 : progress}
      color='adcLightGreen'
      className={cx('progress-bar')}
    />
  )
}

const SellerFlowRoutes: React.FC<SellerFlowRoutesProps> = ({
  featureFlags,
  history,
  initialLeadData,
  initialLeadValidations,
}: SellerFlowRoutesProps) => {
  const { user } = useApplicationContext()

  const standardizedAddress = useMemo(
    () => initialLeadData?.address,
    [initialLeadData?.address]
  )

  // Check if standardizedAddress has at least one non-computed key with a truthy value
  const isAvailableAddress =
    Boolean(standardizedAddress) &&
    Object.keys(standardizedAddress).some(
      key => !key.startsWith('__computed__')
    )

  const initialLeadIntake = useMemo(
    () => getInitialLeadIntake(user, standardizedAddress),
    [user, standardizedAddress]
  )

  const routes: {
    path: string
    step: Step
  }[] = []
  sellerSteps.forEach(step => {
    const newRoute = {
      path: step.path,
      step,
    }
    routes.push(newRoute)
  })

  const nextStepData = {
    answer: {},
    featureFlags,
  } as NextStepData

  const firstStep =
    isAvailableAddress &&
    featureFlags['fe.resi.onboarding.enable-skip-address-step']
      ? (sellerSteps.get(StepId.Address)?.next(nextStepData) as StepId)
      : StepId.Address

  const initialData = {
    featureFlags,
    initialLeadIntake,
    initialStep: sellerSteps.get(firstStep) as Step,
    initialLeadValidations: initialLeadValidations || {},
  }

  return (
    <StepManagerProvider history={history} initialData={initialData}>
      <FlowProgressBar />
      <Switch>
        <Route
          exact
          path={sellerFlowPath}
          render={() => <StepComponent currentStep={initialData.initialStep} />}
        />
        {routes.map(({ path, step }, index) => {
          return (
            <Route
              key={index}
              path={path}
              render={() => <StepComponent currentStep={step} />}
            />
          )
        })}

        <Redirect to={sellerFlowPath} />
      </Switch>
    </StepManagerProvider>
  )
}
export default withRouter(SellerFlowRoutes)
